import React, { useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery } from '@tanstack/react-query';
import luxonPlugin from '@fullcalendar/luxon3';
import { useAuth } from '../../contexts/AuthContext';
import { accountZone, accountDate, dayKey, formatAccountTime } from '../../utils/accountTime';
import { dashboardService, eventService } from '../../services/api';
import EventDetailsModal from './EventDetailsModal';
import CalendarAvailability from './CalendarAvailability';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  PlusIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const viewOptions = [
  { id: 'dayGridMonth', label: 'Month' },
  { id: 'timeGridWeek', label: 'Week' },
  { id: 'timeGridDay', label: 'Day' },
];

const Calendar = ({ selectedDate, onDateSelect, onEventClick }) => {
  const { user } = useAuth();
  const timezone = accountZone(user?.timezone);
  const calendarRef = useRef(null);
  const initialDate = useMemo(
    () => dayKey(selectedDate || new Date(), timezone),
    [selectedDate, timezone]
  );
  const [viewMode, setViewMode] = useState('dayGridMonth');
  const [viewTitle, setViewTitle] = useState(formatAccountTime(initialDate, timezone, 'MMMM yyyy'));
  const [range, setRange] = useState({
    start: accountDate(initialDate, timezone).startOf('month').toJSDate(),
    end: accountDate(initialDate, timezone).startOf('month').plus({ months: 1 }).toJSDate(),
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['calendar-events', timezone, range.start.toISOString(), range.end.toISOString()],
    queryFn: () => dashboardService.getCalendarView(range.start.toISOString(), range.end.toISOString()),
    placeholderData: previous => previous,
  });

  const events = useMemo(() => data?.events || [], [data?.events]);
  const upcoming = useMemo(() => {
    const now = new Date();
    return events
      .filter((event) => (event.allDay ? event.end > dayKey(now, timezone) : accountDate(event.start, timezone).toMillis() >= now.getTime()) && event.extendedProps?.responseStatus !== 'declined')
      .sort((a, b) => accountDate(a.start, timezone).toMillis() - accountDate(b.start, timezone).toMillis())
      .slice(0, 5);
  }, [events, timezone]);

  const aiEnhancedCount = events.filter(
    (event) => event.ai_enhanced || event.aiEnhanced || event.extendedProps?.aiEnhanced
  ).length;

  const openCreateEvent = (date = new Date()) => {
    window.dispatchEvent(new CustomEvent('timelyzer:quick-event', { detail: { date } }));
  };

  const handleDateClick = (info) => {
    onDateSelect?.(info.date);
    openCreateEvent(info.dateStr);
  };

  const handleEventClick = (info) => {
    if (onEventClick) {
      onEventClick(info.event);
      return;
    }

    setSelectedEvent({
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      allDay: info.event.allDay,
    });
    setShowEventModal(true);
  };

  const handleEventDrop = async (info) => {
    if (info.event.extendedProps.connected) {
      info.revert();
      setSelectedEvent({ id: info.event.id, title: info.event.title });
      setShowEventModal(true);
      toast('Open Google Calendar changes to review this reschedule.');
      return;
    }
    try {
      await eventService.updateEvent(info.event.id, {
        start_time: info.event.start?.toISOString(),
        end_time: (info.event.end || info.event.start)?.toISOString(),
        all_day: info.event.allDay ?? false,
        ...(info.event.allDay ? {start_date: info.event.startStr, end_date: info.event.endStr || accountDate(info.event.startStr, timezone).plus({days: 1}).toISODate()} : {}),
      });
      await refetch();
      toast.success('Event rescheduled');
    } catch (eventError) {
      info.revert();
      toast.error('Could not reschedule this event');
    }
  };

  const changeView = (nextView) => {
    setViewMode(nextView);
    calendarRef.current?.getApi().changeView(nextView);
  };

  const goToToday = () => calendarRef.current?.getApi().today();
  const moveCalendar = (direction) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    direction === 'next' ? api.next() : api.prev();
  };

  return (
    <div className="app-page">
      <div className="app-page-inner">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <CalendarDaysIcon className="h-4 w-4" />
              Planning workspace
            </div>
            <h1 className="page-title">Calendar</h1>
            <p className="page-description">
              See your commitments, protect focused time, and open any event to bring its notes and preparation
              context into view.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="status-pill status-pill-success hidden sm:inline-flex">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2f7c5b]" />
              {timezone}
            </span>
            <button onClick={() => openCreateEvent()} className="primary-button">
              <PlusIcon className="h-4 w-4" />
              New event
            </button>
          </div>
        </header>

        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ['Visible events', events.length, CalendarDaysIcon, 'text-[#32699a] bg-[#e2edf6]'],
            ['AI context', aiEnhancedCount, SparklesIcon, 'text-[#246b52] bg-[#dcece4]'],
            ['Coming up', upcoming.length, ClockIcon, 'text-[#996617] bg-[#f7edda]'],
            ['Connected events', events.filter((event) => event.extendedProps?.connected).length, DocumentTextIcon, 'text-[#a94d35] bg-[#f7e7e1]'],
          ].map(([label, value, Icon, tone]) => (
            <div key={label} className="surface-panel flex min-w-0 items-center gap-3 p-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${tone}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-bold uppercase text-[#7b867f]">{label}</p>
                <p className="mt-0.5 truncate text-base font-bold text-[#17211c]">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <CalendarAvailability />

        <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
          <section className="surface-panel min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e2e7e3] px-4 py-3">
              <div className="flex items-center gap-2">
                <button onClick={() => moveCalendar('prev')} className="icon-button" aria-label="Previous period">
                  <ChevronRightIcon className="h-4 w-4 rotate-180" />
                </button>
                <button onClick={() => moveCalendar('next')} className="icon-button" aria-label="Next period">
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                <button onClick={goToToday} className="secondary-button min-h-10 px-3">
                  Today
                </button>
                <h2 className="ml-1 text-base font-bold sm:text-lg">{viewTitle}</h2>
              </div>

              <div className="flex rounded-md border border-[#d8dfda] bg-[#f5f7f5] p-0.5" aria-label="Calendar view">
                {viewOptions.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => changeView(view.id)}
                    className={`min-h-8 rounded px-3 text-xs font-bold transition ${
                      viewMode === view.id ? 'bg-white text-[#1e6049] shadow-sm' : 'text-[#6a756e] hover:text-[#17211c]'
                    }`}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-[640px] p-3 sm:p-4">
              {isLoading ? (
                <div className="flex min-h-[600px] items-center justify-center">
                  <div className="text-center">
                    <ArrowPathIcon className="mx-auto h-6 w-6 animate-spin text-[#246b52]" />
                    <p className="mt-3 text-sm font-medium text-[#65716a]">Loading your calendar</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex min-h-[600px] items-center justify-center">
                  <div className="max-w-sm text-center">
                    <ExclamationCircleIcon className="mx-auto h-8 w-8 text-[#c65f43]" />
                    <h3 className="mt-3 text-base font-bold">Calendar unavailable</h3>
                    <p className="mt-2 text-sm leading-6 text-[#65716a]">
                      We could not load this date range. Check your connection and try again.
                    </p>
                    <button onClick={() => refetch()} className="secondary-button mt-4">Try again</button>
                  </div>
                </div>
              ) : (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin]}
                  timeZone={timezone}
                  initialView="dayGridMonth"
                  initialDate={initialDate}
                  headerToolbar={false}
                  events={events}
                  editable
                  selectable
                  dayMaxEvents
                  nowIndicator
                  height="auto"
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  eventDrop={handleEventDrop}
                  datesSet={(info) => {
                    setViewTitle(info.view.title);
                    setViewMode(info.view.type);
                    setRange({ start: info.start, end: info.end });
                  }}
                  eventContent={(eventInfo) => (
                    <div className="min-w-0 px-0.5 py-0.5">
                      {eventInfo.timeText && <span className="mr-1 opacity-75">{eventInfo.timeText}</span>}
                      <span className="font-semibold">{eventInfo.event.title}</span>
                    </div>
                  )}
                />
              )}
            </div>
          </section>

          <aside className="space-y-4">
            <section className="surface-panel p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase text-[#7c8780]">In this calendar range</p>
                  <h2 className="mt-1 text-base font-bold">Upcoming</h2>
                </div>
                <ClockIcon className="h-5 w-5 text-[#65716a]" />
              </div>

              <div className="mt-4">
                {upcoming.length === 0 ? (
                  <div className="rounded-md bg-[#f4f6f3] px-3 py-5 text-center">
                    <p className="text-sm font-semibold">Your schedule is open</p>
                    <p className="mt-1 text-xs leading-5 text-[#7a857e]">Capture a thought or create an event when you are ready.</p>
                  </div>
                ) : (
                  upcoming.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => {
                        setSelectedEvent({ id: event.id, title: event.title });
                        setShowEventModal(true);
                      }}
                      className="flex w-full items-start gap-3 border-b border-[#edf0ed] py-3 text-left last:border-0"
                    >
                      <span className="mt-0.5 flex w-10 shrink-0 flex-col items-center rounded-md bg-[#edf3ef] px-1 py-1.5">
                        <span className="text-[9px] font-bold uppercase text-[#5f6b64]">
                          {formatAccountTime(event.start, timezone, 'MMM')}
                        </span>
                        <span className="text-sm font-bold text-[#246b52]">{formatAccountTime(event.start, timezone, 'd')}</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{event.title}</span>
                        <span className="mt-1 block text-xs text-[#7a857e]">
                          {event.allDay ? 'All day' : formatAccountTime(event.start, timezone, 'h:mm a')}
                        </span>
                      </span>
                      <ChevronRightIcon className="mt-2 h-4 w-4 text-[#9aa39e]" />
                    </button>
                  ))
                )}
              </div>
            </section>

            <section className="surface-panel overflow-hidden">
              <div className="border-b border-[#e8ece9] bg-[#f5f8f6] p-4">
                <div className="status-pill status-pill-info">
                  <SparklesIcon className="h-3.5 w-3.5" />
                  Planning context
                </div>
                <h2 className="mt-3 text-base font-bold">Capture before it gets lost</h2>
                <p className="mt-2 text-xs leading-5 text-[#6d7871]">
                  Add the thought first. You can decide whether it belongs on your calendar after Timelyzer reviews it.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4">
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:quick-note'))}
                  className="secondary-button min-h-10 px-3 text-xs"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  Quick note
                </button>
                <button onClick={() => openCreateEvent()} className="primary-button min-h-10 px-3 text-xs">
                  <PlusIcon className="h-4 w-4" />
                  New event
                </button>
              </div>
            </section>
          </aside>
        </div>
      </div>

      <EventDetailsModal
        event={selectedEvent}
        open={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
        onUpdate={() => refetch()}
      />
    </div>
  );
};

export default Calendar;
